/**
 * TransactionsPage — Phase 7
 * My transactions (buyer/seller) with escrow actions.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Clock, ShieldCheck, X, AlertTriangle } from "lucide-react";

import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { txAPI, TRANSACTION_STATUS_LABELS, disputesV2API } from "../lib/auctionsV2";
import { FEATURES } from "../lib/features";

const TONE = {
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  blue:  "bg-blue-50 text-blue-700 border-blue-200",
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  red:   "bg-red-50 text-red-700 border-red-200",
  gray:  "bg-neutral-100 text-neutral-700 border-neutral-200",
};

export function TransactionsListPage() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [role, setRole] = useState("any");
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    txAPI.mine({ role, limit: 50 }).then(setData).catch(() => setData({ total: 0, items: [] })).finally(() => setLoading(false));
  }, [role]);

  if (!user) return <div className="p-10 text-center">Please log in.</div>;

  return (
    <div className="min-h-[calc(100vh-72px)] bg-[#faf9f5]" dir={lang === "ar" ? "rtl" : "ltr"}
         data-testid="transactions-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8a6f2e] font-semibold mb-1">
            {lang === "ar" ? "الحساب" : "Account"}
          </div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-[#0a0a0a]">
            {lang === "ar" ? "معاملاتي" : "My Transactions"}
          </h1>
        </div>

        <div className="flex gap-2 mb-4">
          {[
            { k: "any", ar: "الكل", en: "All" },
            { k: "buyer", ar: "كمشتري", en: "As Buyer" },
            { k: "seller", ar: "كبائع", en: "As Seller" },
          ].map((r) => (
            <button type="button" key={r.k} onClick={() => setRole(r.k)}
                    data-testid={`tx-role-${r.k}`}
                    className={`px-3.5 py-1.5 rounded-full text-[12px] font-bold ${
                      role === r.k ? "bg-[#0a0a0a] text-[#f7eecd]" : "bg-white border border-[#0a0a0a]/10 text-[#525252]"
                    }`}>
              {lang === "ar" ? r.ar : r.en}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-[#8a8a8a]">…</div>
        ) : data.items.length === 0 ? (
          <div className="p-16 text-center rounded-2xl bg-white border border-dashed border-[#0a0a0a]/10"
               data-testid="tx-empty">
            <ShieldCheck className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <div className="text-sm text-[#8a8a8a]">
              {lang === "ar" ? "لا توجد معاملات بعد" : "No transactions yet"}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {data.items.map((t) => {
              const lbl = TRANSACTION_STATUS_LABELS[t.status] || {};
              const isBuyer = t.buyer_user_id === user.id;
              return (
                <Link key={t.id} to={`/transactions/${t.id}`}
                      className="block rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-4 hover:border-[#c9a55c]/50 transition-colors"
                      data-testid={`tx-row-${t.id}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex-1 min-w-[220px]">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-mono text-[12px] text-[#8a6f2e] font-bold">{t.reference}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${TONE[lbl.tone] || TONE.gray}`}>
                          {lang === "ar" ? lbl.ar : lbl.en}
                        </span>
                        <span className="text-[10px] text-[#8a8a8a]">
                          {isBuyer ? (lang === "ar" ? "شراء" : "Purchase") : (lang === "ar" ? "بيع" : "Sale")}
                        </span>
                      </div>
                      <div className="text-[13px] text-[#525252]">
                        {lang === "ar" ? "المصدر" : "Source"}: {t.source} · {new Date(t.created_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-US")}
                      </div>
                    </div>
                    <div className="text-end">
                      <div className="text-lg font-bold text-[#0a0a0a]">
                        {Number(t.total_sar).toLocaleString()} <span className="text-[11px] text-[#8a8a8a]">ر.س</span>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-[#8a8a8a]" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export function TransactionDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showDispute, setShowDispute] = useState(false);
  const [dspSubject, setDspSubject] = useState("");
  const [dspDesc, setDspDesc] = useState("");
  const [dspKind, setDspKind] = useState("item_not_as_described");

  const load = useCallback(() => txAPI.get(id).then(setT).catch(() => setT(null)), [id]);
  useEffect(() => { load(); }, [load]);

  if (!user) return <div className="p-10 text-center">Please log in.</div>;
  if (!t) return <div className="p-10 text-center">…</div>;

  const lbl = TRANSACTION_STATUS_LABELS[t.status] || {};
  const isBuyer = t.buyer_user_id === user.id;
  const isSeller = t.seller_user_id === user.id;

  const doAction = async (fn, action) => {
    setBusy(true);
    try {
      await fn();
      toast.success(lang === "ar" ? "تم بنجاح" : "Done");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === "ar" ? "فشل" : "Failed"));
    } finally {
      setBusy(false);
    }
  };

  const openDispute = async () => {
    setBusy(true);
    try {
      const other = isBuyer ? t.seller_user_id : t.buyer_user_id;
      const d = await disputesV2API.open({
        transaction_id: t.id,
        opened_against_user_id: other,
        kind: dspKind,
        subject: dspSubject,
        description: dspDesc,
      });
      toast.success(lang === "ar" ? "تم فتح النزاع" : "Dispute opened");
      navigate(`/disputes-v2/${d.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === "ar" ? "فشل" : "Failed"));
    } finally {
      setBusy(false);
    }
  };

  const STEPS = [
    { key: "reserved",   ar: "محجوز",       en: "Reserved"   },
    { key: "paid",       ar: "مدفوع",       en: "Paid"       },
    { key: "processing", ar: "قيد التنفيذ", en: "Processing" },
    { key: "completed",  ar: "مكتمل",       en: "Completed"  },
  ];
  const currentIdx = STEPS.findIndex((s) => s.key === t.status);

  return (
    <div className="min-h-[calc(100vh-72px)] bg-[#faf9f5]" dir={lang === "ar" ? "rtl" : "ltr"}
         data-testid="transaction-detail">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-5">
        {/* Header */}
        <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-6">
          <div className="flex items-start flex-wrap gap-3 mb-4">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8a6f2e] font-bold mb-1">
                {lang === "ar" ? "معاملة" : "Transaction"}
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="font-mono text-[15px] font-bold text-[#0a0a0a]">{t.reference}</div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${TONE[lbl.tone] || TONE.gray}`}>
                  {lang === "ar" ? lbl.ar : lbl.en}
                </span>
              </div>
            </div>
            <div className="text-end">
              <div className="text-2xl font-bold text-[#0a0a0a]">
                {Number(t.total_sar).toLocaleString()} <span className="text-sm text-[#8a8a8a]">ر.س</span>
              </div>
            </div>
          </div>

          {/* Progress rail */}
          {!["canceled", "refunded"].includes(t.status) && (
            <div className="grid grid-cols-4 gap-1 mt-3">
              {STEPS.map((s, i) => {
                const done = i <= currentIdx;
                return (
                  <div key={s.key} className="text-center">
                    <div className={`h-1.5 rounded-full mb-1 ${done ? "bg-emerald-500" : "bg-neutral-200"}`} />
                    <div className={`text-[10px] font-bold ${done ? "text-emerald-700" : "text-neutral-400"}`}>
                      {lang === "ar" ? s.ar : s.en}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-5">
          <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e] mb-3">
            {lang === "ar" ? "الإجراءات" : "Actions"}
          </div>
          <div className="flex flex-wrap gap-2">
            {isBuyer && t.status === "reserved" && (
              <button type="button" onClick={() => doAction(() => txAPI.pay(t.id), "pay")}
                      disabled={busy}
                      className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-[12px] font-bold text-[#0a0a0a]"
                      style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)" }}
                      data-testid="tx-pay">
                <ShieldCheck className="w-3.5 h-3.5" />
                {FEATURES.ESCROW_ENABLED
                  ? (lang === "ar" ? "تأكيد الدفع (Escrow)" : "Pay to Escrow")
                  : (lang === "ar" ? "تأكيد الدفع" : "Confirm Payment")}
              </button>
            )}
            {isSeller && t.status === "paid" && (
              <button type="button" onClick={() => doAction(() => txAPI.processing(t.id), "processing")}
                      disabled={busy}
                      className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-[12px] font-bold text-white bg-blue-600"
                      data-testid="tx-processing">
                <Clock className="w-3.5 h-3.5" /> {lang === "ar" ? "بدء التسليم" : "Start Delivery"}
              </button>
            )}
            {isBuyer && ["processing", "paid"].includes(t.status) && (
              <button type="button" onClick={() => doAction(() => txAPI.confirm(t.id), "confirm")}
                      disabled={busy}
                      className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-[12px] font-bold text-white bg-emerald-600"
                      data-testid="tx-confirm">
                <CheckCircle2 className="w-3.5 h-3.5" /> {lang === "ar" ? "تأكيد الاستلام وتحرير الدفع" : "Confirm & Release"}
              </button>
            )}
            {!["completed", "canceled", "refunded"].includes(t.status) && (
              <>
                <button type="button" onClick={() => doAction(() => txAPI.cancel(t.id, "user_cancel"))}
                        disabled={busy}
                        className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-[12px] font-bold text-red-700 bg-red-50 border border-red-200"
                        data-testid="tx-cancel">
                  <X className="w-3.5 h-3.5" /> {lang === "ar" ? "إلغاء" : "Cancel"}
                </button>
                <button type="button" onClick={() => setShowDispute(true)}
                        disabled={busy}
                        className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-[12px] font-bold text-amber-800 bg-amber-50 border border-amber-200"
                        data-testid="tx-dispute">
                  <AlertTriangle className="w-3.5 h-3.5" /> {lang === "ar" ? "فتح نزاع" : "Open Dispute"}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Status history */}
        <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-5">
          <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e] mb-3">
            {lang === "ar" ? "سجل الحالات" : "Status history"}
          </div>
          <div className="divide-y divide-[#0a0a0a]/[0.06]">
            {(t.status_history || []).map((h, i) => (
              <div key={i} className="py-2 flex items-center justify-between text-[13px]">
                <span className="font-semibold">{TRANSACTION_STATUS_LABELS[h.status]?.[lang] || h.status}</span>
                <span className="text-[11px] text-[#8a8a8a]">
                  {new Date(h.at).toLocaleString(lang === "ar" ? "ar-SA" : "en-US")}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Dispute modal */}
      {showDispute && (
        <>
          <button type="button" aria-label="close" onClick={() => setShowDispute(false)}
                  className="fixed inset-0 z-40 bg-black/40" />
          <div className="fixed inset-x-4 top-20 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:max-w-md z-50 rounded-2xl bg-white shadow-2xl p-5 max-h-[92vh] overflow-y-auto"
               data-testid="dispute-modal">
            <h3 className="text-lg font-bold mb-3">{lang === "ar" ? "فتح نزاع" : "Open dispute"}</h3>
            <div className="space-y-3">
              <select value={dspKind} onChange={(e) => setDspKind(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm bg-white"
                      data-testid="dispute-kind">
                <option value="item_not_delivered">{lang === "ar" ? "لم يُسلّم" : "Not delivered"}</option>
                <option value="item_not_as_described">{lang === "ar" ? "لا يطابق الوصف" : "Not as described"}</option>
                <option value="payment_issue">{lang === "ar" ? "مشكلة دفع" : "Payment issue"}</option>
                <option value="auction_fraud">{lang === "ar" ? "احتيال" : "Fraud"}</option>
                <option value="other">{lang === "ar" ? "أخرى" : "Other"}</option>
              </select>
              <input type="text" value={dspSubject} onChange={(e) => setDspSubject(e.target.value)}
                     placeholder={lang === "ar" ? "الموضوع" : "Subject"}
                     className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm"
                     data-testid="dispute-subject" required />
              <textarea rows={4} value={dspDesc} onChange={(e) => setDspDesc(e.target.value)}
                        placeholder={lang === "ar" ? "الوصف التفصيلي" : "Description"}
                        className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm"
                        data-testid="dispute-desc" required />
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowDispute(false)}
                        className="px-4 py-2 rounded-full text-sm text-[#525252]">
                  {lang === "ar" ? "إلغاء" : "Cancel"}
                </button>
                <button type="button" onClick={openDispute} disabled={busy || !dspSubject || !dspDesc}
                        className="px-4 py-2 rounded-full text-sm font-bold text-white bg-red-600 disabled:opacity-40"
                        data-testid="dispute-open">
                  {lang === "ar" ? "فتح النزاع" : "Open dispute"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
